// Methodology surfaces (PLAN-P3 D3 + D3b) — implementation programs, PDCA schedules, improvement points
// (verbeterpunten) as full-CRUD lists, plus the sprint-gate decision log (D3b) + the ChangeTrigger feed.
// Source: CRUD-CAPTURE-PROTOCOL, B5B-METHODOLOGY-SURFACES.
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AggregateCrud } from "../lib/aggregate-crud";
import { api } from "../lib/api";
import { Can } from "../lib/rbac";
import { humanizeEnum } from "../lib/enum-labels";
import { TENANT_PATHS } from "@sympllex/api-client";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export function ProgramsRoute() {
  const navigate = useNavigate();
  return (
    <AggregateCrud
      title="Implementatieprogramma's" queryKey="implementation-programs" basePath={TENANT_PATHS.implementationPrograms} permPrefix="implementation_program"
      listFn={() => api.listImplementationPrograms()} createFn={(i) => api.createImplementationProgram(i as unknown as Parameters<typeof api.createImplementationProgram>[0])}
      fields={[{ key: "name", label: "Name", type: "text", required: true }, { key: "description", label: "Description", type: "textarea" }]}
      empty="No implementation programs yet."
      onRowOpen={(r) => { if (r.id) void navigate({ to: "/programs/$id", params: { id: String(r.id) } }); }}
    />
  );
}
export function PdcaRoute() {
  return (
    <AggregateCrud
      title="PDCA-schema's" queryKey="pdca-schedules" basePath={TENANT_PATHS.pdcaSchedules} permPrefix="pdca_schedule"
      listFn={() => api.listPdcaSchedules()} createFn={(i) => api.createPdcaSchedule(i as unknown as Parameters<typeof api.createPdcaSchedule>[0])}
      fields={[
        { key: "name", label: "Name", type: "text", required: true },
        { key: "cadence", label: "Cadence", type: "select", options: ["monthly", "quarterly", "semi_annual", "annual", "trigger_only"], required: true },
        { key: "description", label: "Description", type: "textarea" },
      ]}
      empty="No PDCA schedules yet."
    />
  );
}
export function VerbeterpuntenRoute() {
  const navigate = useNavigate();
  return (
    <AggregateCrud
      title="Verbeterpunten" queryKey="verbeterpunten" basePath={TENANT_PATHS.verbeterpunten} permPrefix="implementation_program"
      listFn={() => api.listVerbeterpunten()} createFn={(i) => api.createVerbeterpunt(i as unknown as Parameters<typeof api.createVerbeterpunt>[0])}
      fields={[
        { key: "title", label: "Title", type: "text", required: true },
        { key: "sourceTier", label: "Source tier (1=regulatory, 2=framework, 3=tenant)", type: "number", required: true },
        { key: "description", label: "Description", type: "textarea" },
      ]}
      empty="No improvement points yet."
      onRowOpen={(r) => { if (r.id) void navigate({ to: "/verbeterpunten/$id", params: { id: String(r.id) } }); }}
    />
  );
}

// D3b — sprint-gate decision log: go/no-go decisions on an implementation program's sprint stages.
const SGD_DECISIONS = ["approved", "approved_with_conditions", "deferred", "rejected"];
export function SprintGatesRoute() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["sprint-gates"] });
  const { data, isLoading, error } = useQuery({ queryKey: ["sprint-gates"], queryFn: () => api.listSprintGateDecisions() as Promise<Array<Record<string, unknown>>> });
  const rows = data ?? [];
  return (
    <section>
      <PageHeader title="Sprint-gate beslissingen" description="(go/no-go log)" />
      <Can perm="sprint_gate_decision.sign_off"><CreateSprintGate onDone={invalidate} /></Can>
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
      {!isLoading && !error && rows.length === 0 && <p className="text-sm text-muted-foreground">No sprint-gate decisions yet.</p>}
      {rows.length > 0 && (
        <div className="mt-2 rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>{["Sprint", "Stage", "Decision", "Decided", "Rationale"].map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={String(r.id)} className="cursor-pointer" onClick={() => { if (r.id) void navigate({ to: "/sprint-gates/$id", params: { id: String(r.id) } }); }}>
                  <TableCell className="align-top">{String(r.sprintNumber ?? "—")}</TableCell>
                  <TableCell className="align-top">{String(r.sprintStageCode ?? "—")}</TableCell>
                  <TableCell className="align-top"><DecisionBadge decision={String(r.decision)} /></TableCell>
                  <TableCell className="align-top">{r.decidedAt ? new Date(String(r.decidedAt)).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="align-top">{String(r.rationale ?? "")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
function CreateSprintGate({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState<Record<string, string>>({ decision: "approved" });
  const set = (k: string, v: string) => setF((s) => ({ ...s, [k]: v }));
  const programs = (useQuery({ queryKey: ["implementation-programs"], queryFn: () => api.listImplementationPrograms() }).data ?? []) as Array<Record<string, unknown>>;
  const mut = useMutation({
    mutationFn: () => api.createSprintGateDecision({ implementationProgramId: f.implementationProgramId!, sprintNumber: f.sprintNumber!, sprintStageCode: f.sprintStageCode!, decision: f.decision!, rationale: f.rationale! }),
    onSuccess: () => { setF({ decision: "approved" }); setOpen(false); onDone(); },
  });
  const ready = f.implementationProgramId && f.sprintNumber?.trim() && f.sprintStageCode?.trim() && f.rationale?.trim();
  if (!open) return <Button onClick={() => setOpen(true)}>+ Record decision</Button>;
  return (
    <Card className="my-3 max-w-md">
      <CardContent className="flex flex-col gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="sg-program">Program *</Label>
          <Select value={f.implementationProgramId ?? ""} onValueChange={(v) => set("implementationProgramId", v)}>
            <SelectTrigger id="sg-program" className="w-full"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {programs.map((p) => <SelectItem key={String(p.id)} value={String(p.id)}>{String(p.name)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="sg-sprint">Sprint number *</Label>
          <Input id="sg-sprint" value={f.sprintNumber ?? ""} onChange={(e) => set("sprintNumber", e.target.value)} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="sg-stage">Stage code *</Label>
          <Input id="sg-stage" value={f.sprintStageCode ?? ""} onChange={(e) => set("sprintStageCode", e.target.value)} placeholder="e.g. design_gate" />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="sg-decision">Decision *</Label>
          <Select value={f.decision ?? "approved"} onValueChange={(v) => set("decision", v)}>
            <SelectTrigger id="sg-decision" className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SGD_DECISIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="sg-rationale">Rationale *</Label>
          <Textarea id="sg-rationale" value={f.rationale ?? ""} onChange={(e) => set("rationale", e.target.value)} rows={2} />
        </div>
        {programs.length === 0 && <p className="text-sm text-amber-800">Create an implementation program first.</p>}
        {mut.isError && <p className="text-sm text-destructive">{(mut.error as Error).message}</p>}
        <div className="flex gap-2">
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !ready}>{mut.isPending ? "…" : "Record"}</Button>
          <Button variant="outline" onClick={() => { setOpen(false); mut.reset(); }}>Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// D3b — ChangeTrigger feed: regulatory changes detected by the RSS/MCP pipeline (read-only).
export function ChangeTriggersRoute() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({ queryKey: ["change-triggers"], queryFn: () => api.listChangeTriggers() as Promise<Array<Record<string, unknown>>> });
  const rows = data ?? [];
  return (
    <section>
      <PageHeader title="Wijzigingssignalen" description="Regelgevingswijzigingen gedetecteerd door de RSS/MCP-pijplijn. Read-only." />
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
      {!isLoading && !error && rows.length === 0 && <p className="text-sm text-muted-foreground">No change triggers detected yet.</p>}
      {rows.length > 0 && (
        <ul className="max-w-3xl list-none p-0">
          {rows.map((r) => (
            <li key={String(r.id)} className="border-b">
              <button type="button" className="block w-full cursor-pointer py-2.5 text-left hover:bg-muted/40"
                onClick={() => { if (r.id) void navigate({ to: "/change-triggers/$id", params: { id: String(r.id) } }); }}>
                <div className="text-sm font-semibold">{String(r.title)}</div>
                <div className="text-sm text-muted-foreground">{String(r.summary ?? "")}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{humanizeEnum(r.sourceKind)} · {humanizeEnum(r.cascadeScope)} · {r.detectedAt ? new Date(String(r.detectedAt)).toLocaleString() : ""} · fan-out: {humanizeEnum(r.fanOutStatus)}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DecisionBadge({ decision }: { decision: string }) {
  const variant = decision === "approved" ? "default"
    : decision === "approved_with_conditions" || decision === "deferred" ? "secondary"
    : "destructive";
  return <Badge variant={variant}>{decision}</Badge>;
}
